import { ToastType } from '@components/Toast/Toast';
import { createAppStore } from '@store/createAppStore';
import {
  fetchLtlReferenceNumbers,
  fetchQuoteParameters,
  LtlReferenceNumberUpdate,
} from '@store/ltl/services';
import {
  AccessorialState,
  LoadItemCalculationState,
  LoadItemCalculationTotals,
  LTLLoadItem,
  LTLQuote,
  LTLQuoteState,
  LTLQuotingResultState,
  ltlQuotingViewState,
  LTLReferenceNumber,
  LTLReferenceNumberTypes,
  LTLTrackingData,
  LTLTrackingDataState,
  QuoteFormState,
} from '@store/ltl/types';
import { LtlQuoteParametersViewModel } from '@store/orders/types';
import type { ChangeTrackingSource } from '@typeDefinitions/changeTrackingTypes';
import { printLog, printStackTrace } from '@utils/printUtils';
import { handleToast } from '@utils/utils';
import { produce } from 'solid-js/store';

const [quoteFormStore, setQuoteFormStore, resetQuoteFormStore] =
  createAppStore<QuoteFormState>(
    {
      quoteParametersId: null,
      loadId: null,
      resetForm: false,
    },
    'quoteFormStore',
  );

const [accessorialStore, setAccessorialStore, resetAccessorialStore] =
  createAppStore<AccessorialState>(
    {
      isLoading: true,
      isError: false,
      errorMessage: '',
      items: [],
      overlay: false,
    },
    'accessorialStore',
  );

const [
  ltlQuotingResultStore,
  setLTLQuotingResultStore,
  resetLTLQuotingResultStore,
] = createAppStore<LTLQuotingResultState>(
  {
    isLoading: false,
    isError: false,
    errorMessage: '',
    items: [],
    overlay: false,
  },
  'ltlQuotingResultStore',
);

const [ltlQuoteState, setLtlQuoteState, resetLtlQuoteState] =
  createAppStore<LTLQuoteState>(
    {
      isLoading: false,
      isError: false,
      errorMessage: '',
      quote: undefined,
      overlay: false,
      falveyPricingQuote: undefined,
      declaredValue: undefined,
    },
    'ltlQuoteState',
  );

// @ts-expect-error Exposing the LTL quote to the state.
window.ltlQuote = { ltlQuoteState, setLtlQuoteState };

export const getInitialLoadItemCalcTotals = (): LoadItemCalculationTotals => {
  return {
    totalLinearFeet: 0,
    totalCubicFeet: 0,
    totalPcf: 0,
    totalAdjustedPcf: 0,
    totalAdjustedCubicFeet: 0,
    loadItemCalculations: [],
    warnings: [],
  };
};

const [loadItemCalculationState, setLoadItemCalculationState] =
  createAppStore<LoadItemCalculationState>(
    {
      isLoading: false,
      isError: false,
      errorMessage: '',
      items: getInitialLoadItemCalcTotals(),
      overlay: false,
    },
    'loadItemCalculationState',
  );

const [
  ltlReferenceNumberStore,
  setLtlReferenceNumberStore,
  resetLtlReferenceNumberStore,
] = createAppStore<LTLTrackingDataState>(
  {
    isLoading: false,
    isSaving: false,
    isError: false,
    errorMessage: '',
    operationType: 'None',
    trackingData: {
      referenceNumbers: [],
      trackingUrlTemplate: '',
      loadId: 0,
    },
  },
  'ltlReferenceNumberStore',
);

const [
  quoteParametersStore,
  setQuoteParametersStore,
  resetQuoteParametersStore,
] = createAppStore(
  {
    isLoading: false,
    isError: false,
    errorMessage: '',
    quoteParameters: [] as LtlQuoteParametersViewModel[],
  },
  'quoteParametersStore',
);

const loadQuoteParameters = async (isHistory: boolean, customerId?: number) => {
  setQuoteParametersStore({
    isLoading: true,
    isError: false,
    errorMessage: '',
  });

  try {
    const data = await fetchQuoteParameters(isHistory, customerId);
    setQuoteParametersStore(
      produce((draft) => {
        draft.quoteParameters = data;
        draft.isLoading = false;
      }),
    );
  } catch (error) {
    printLog('Error loading quote parameters:', error);
    setQuoteParametersStore({
      isError: true,
      errorMessage: 'Failed to load quote parameters',
      isLoading: false,
    });
  }
};

const setLoadItemCalculation = (
  loadItemCalculation: LoadItemCalculationTotals,
) => {
  try {
    setLoadItemCalculationState(
      produce((draft: LoadItemCalculationState) => {
        draft.items = loadItemCalculation;
        draft.isLoading = false;
      }),
    );
  } catch (error) {
    printLog('Error setting load item calculation:', error);

    setLoadItemCalculationState(
      produce((draft: LoadItemCalculationState) => {
        draft.isError = true;
        draft.errorMessage = 'Failed loading load item calculation';
        draft.isLoading = false;
      }),
    );
  }
};

const resetLoadItemCalculation = () => {
  setLoadItemCalculationState(
    produce((draft: LoadItemCalculationState) => {
      draft.items = getInitialLoadItemCalcTotals();
      draft.isLoading = false;
      draft.isError = false;
      draft.errorMessage = '';
    }),
  );
};

const setLTLQuote = (ltlQuote: LTLQuote, requote?: boolean) => {
  setLtlQuoteState(
    produce((draft: LTLQuoteState) => {
      draft.quote = ltlQuote;
      draft.requote = requote ?? false;
    }),
  );
};

const setCustomerInfo = (customerId: number, customerName: string) => {
  setLtlQuoteState(
    produce((draft: LTLQuoteState) => {
      if (draft.quote) {
        draft.quote.customerId = customerId;
        draft.quote.customerName = customerName;
      }
    }),
  );
};

const setOriginOrDestinationInfo = (info: {
  type: 'Origin' | 'Destination';
  city?: string | null;
  state?: string | null;
  zip: string;
  country?: string | null;
}) => {
  printStackTrace('setOriginOrDestinationInfo');
  setLtlQuoteState(
    produce((draft: LTLQuoteState) => {
      if (draft.quote) {
        if (info.type === 'Origin') {
          draft.quote.originCity = info.city;
          draft.quote.originState = info.state;
          draft.quote.originZip = info.zip;
          draft.quote.originCountry = info.country;
        } else {
          draft.quote.destinationCity = info.city;
          draft.quote.destinationState = info.state;
          draft.quote.destinationZip = info.zip;
          draft.quote.destinationCountry = info.country;
        }
      }
    }),
  );
};

const setLoadItems = (items: LTLLoadItem[]) => {
  printStackTrace('setLoadItems');
  setLtlQuoteState(
    produce((draft: LTLQuoteState) => {
      if (draft.quote) {
        draft.quote.loadItems = items;
      }
    }),
  );
};

const setPickupDate = (date: Date | string) => {
  printStackTrace('setPickupDate');
  setLtlQuoteState(
    produce((draft: LTLQuoteState) => {
      if (draft.quote) {
        draft.quote.pickupDate = date;
      }
    }),
  );
};

const fetchReferenceNumbers = async (
  loadId: number,
  cb?: (refNumbers: LTLReferenceNumber[]) => void,
) => {
  try {
    setLtlReferenceNumberStore('isLoading', true);
    const resp = (await fetchLtlReferenceNumbers(loadId)) as LTLTrackingData;
    if (typeof resp === 'object') {
      setLtlReferenceNumberStore(
        produce((draft: LTLTrackingDataState) => {
          draft.trackingData = {
            referenceNumbers: resp.referenceNumbers,
            trackingUrlTemplate: resp.trackingUrlTemplate,
            loadId: loadId,
          };
        }),
      );
      cb && cb(resp.referenceNumbers);
    }
  } catch (e) {
    printLog('Error fetching reference numbers:', e);
    setLtlReferenceNumberStore(
      produce((draft: LTLTrackingDataState) => {
        draft.isError = true;
        draft.errorMessage = 'Failed loading reference numbers';
      }),
    );
  } finally {
    setLtlReferenceNumberStore('isLoading', false);
  }
};

const updateReferenceNumberStore = (
  type: LTLReferenceNumberTypes,
  value: string,
) => {
  setLtlReferenceNumberStore(
    produce((draft: LTLTrackingDataState) => {
      draft.operationType = 'Update';
      draft.trackingData = {
        ...ltlReferenceNumberStore.trackingData,
        referenceNumbers:
          ltlReferenceNumberStore.trackingData.referenceNumbers.map(
            (refNumber) =>
              refNumber.type === type
                ? {
                    ...refNumber,
                    value,
                  }
                : refNumber,
          ),
      };
    }),
  );
};

const saveReferenceNumbers = async (
  changeTrackingSource?: ChangeTrackingSource,
): Promise<boolean> => {
  const referenceNumbers =
    ltlReferenceNumberStore.trackingData.referenceNumbers.filter(
      (referenceNumber) =>
        referenceNumber.type !== LTLReferenceNumberTypes.BOL &&
        referenceNumber.value.trim() !== '',
    );

  try {
    const response = await LtlReferenceNumberUpdate(
      ltlReferenceNumberStore.trackingData.loadId,
      referenceNumbers,
      changeTrackingSource,
    );

    if (response ?? false) {
      handleToast(ToastType.Success, 'LTL Reference Numbers Saved.');
      return true;
    }

    handleToast(ToastType.Error, 'Failed to save LTL Reference Numbers.');
    return false;
  } catch (e) {
    printLog('Error saving reference numbers:', e);
    setLtlReferenceNumberStore(
      produce((draft: LTLTrackingDataState) => {
        draft.isError = true;
        draft.errorMessage = 'Failed saving reference numbers';
      }),
    );
    return false;
  }
};

const [ltlQuotingViewStore, setLtlQuotingViewStore] =
  createAppStore<ltlQuotingViewState>(
    {
      currentLTLView: 'quote-form',
    },
    'ltlQuotingViewStore',
  );

export {
  accessorialStore,
  fetchReferenceNumbers,
  loadItemCalculationState,
  loadQuoteParameters,
  ltlQuoteState,
  ltlQuotingResultStore,
  ltlQuotingViewStore,
  ltlReferenceNumberStore,
  quoteFormStore,
  quoteParametersStore,
  resetAccessorialStore,
  resetLtlQuoteState,
  resetLTLQuotingResultStore,
  resetLtlReferenceNumberStore,
  resetQuoteFormStore,
  resetQuoteParametersStore,
  saveReferenceNumbers,
  setLtlReferenceNumberStore,
  setAccessorialStore,
  setCustomerInfo,
  setLoadItemCalculation,
  setLoadItems,
  setLTLQuote,
  setLtlQuoteState,
  setLTLQuotingResultStore,
  setLtlQuotingViewStore,
  setOriginOrDestinationInfo,
  setPickupDate,
  setQuoteFormStore,
  setQuoteParametersStore,
  updateReferenceNumberStore,
  resetLoadItemCalculation,
};
